import { BenefitsPaymentsCentreApi } from '~/api/benefits-payments-centre-api';
import { AppRouteID } from '~/app-routes';
import {
	autoinject,
	computedFrom,
	observable,
	TaskQueue,
} from 'aurelia-framework';
import { ValidationController } from 'aurelia-validation';
import { ClassicPanel } from '~/components/classic-panel/classic-panel';
import { ClassicPanelDialogUtils } from '~/components/classic-panel/classic-panel-dialog-utils';
import { ContextMenuItem } from '~/components/context-menu-list/context-menu-list';
import { FormType } from '~/components/form/form';
import {
	getErrorOrWarningStatus,
	getNoteIconStatus,
	IconStatusError,
	IconStatusItem,
	IconStatusList,
} from '~/components/icon-status-list/icon-status-list';
import { Notification } from '~/components/notification/notification';
import { ProgressBar } from '~/components/progress-bar/progress-bar';
import { TabbedPanels } from '~/components/tabbed-panels/tabbed-panels';
import { CommonURLParam, ShortcutKey } from '~/constants';
import { RestoreBatchDialog } from '~/forms/batch-processing-centre/restore-batch-dialog';
import {
	NewBenefitPaymentDialog,
	NewBenefitPaymentDialogModel,
} from '~/forms/benefits-payments-centre/new-benefit-payment-dialog';
import {
	BenefitsPaymentsProcessBatchDialog,
	BenefitsPaymentsProcessBatchDialogModel,
} from '~/forms/benefits-payments-centre/benefits-payments-process-batch-dialog';
import {
	RedemptionInputsDialog,
	RedemptionInputsDialogModel,
} from '~/forms/benefits-payments-centre/redemption-inputs-dialog';
import ClassicForm from '~/forms/classic-form';
import { CustomisationEditorSelectDialogManager } from '~/forms/customisation-editor/customisation-editor-select-dialog-manager';
import { CustomisationEditorSelectDialogModel } from '~/forms/customisation-editor/customisation-editor-select-dialog';
import { HiddenTarget } from '~/models/column-definition';
import { ApiCorrelationId } from '~/models/entities/api-correlation-id';
import { FormModel, Modifiable } from '~/models/form-model';
import {
	BatchProcess,
	BatchProcessFieldName,
	BatchProgressStatus,
} from '~/models/entities/batch-process';
import { FormParameters, FormParameterType } from '~/models/form-parameters';
import { InsertResult } from '~/models/insert-result';
import { BLANK_MEMBER } from '~/models/membership';
import { createFilterModel } from '~/models/filter-model';
import {
	ReportOutputType,
	ReportRecordType,
	ReportResult,
} from '~/models/report-model';
import { DialogUtils } from '~/utils/dialog-utils';
import { FetchUtils } from '~/utils/fetch-utils';
import FormModelUtils from '~/utils/form-model-utils';
import { KeyboardShortcut } from '~/utils/keyboard-shortcut';
import { Logger } from '~/utils/logger';
import { ReportOutputUtils } from '~/utils/report-output-utils';
import { generateLinkNoCustomisationParam } from '~/utils/router-utils';
import { ClassicPanelDialogModel } from '~/components/classic-panel/classic-panel-dialog';

enum BatchProgressParentChildIndicator {
	CHILD = 'C',
	PARENT = 'P',
}

@autoinject
export class BenefitsPaymentsCentre extends ClassicForm {
	/**
	 * progressBar is a React component that is not attached to the main DOM
	 * and we just get the HTML for the DataTable each render.
	 *
	 * This is copied from BatchProcessingCentre. It should not be copied. WEBUI-4085 has been raised to refactor
	 * this hack since it now appears in two places.
	 *
	 * @deprecated This is a dirty hack, do not copy
	 */
	private readonly progressBar = new ProgressBar(undefined);

	/**
	 * Context menu IDs
	 */
	private readonly contextMenuIds = {
		processBatchMenu: 'processBatchMenuId',
		restoreBatchMenu: 'restoreBatchMenuId',
		batchMessagesMenu: 'batchMessagesMenuId',
		transactionCentreMenu: 'transactionCentreMenuId',
		reportingCentreMenu: 'reportingCentreMenuId',
		viewSplitGroupMenu: 'viewSplitGroupMenuId',
		redemptionInputsMemu: 'redemptionInputsMemuId',
	};

	/**
	 * Table filter flag for "Show Incomplete Batches"
	 */
	@observable private isShowIncompleteBatchesOnly: boolean = false;

	/**
	 * Table filter flag for "Show My Batches Only"
	 */
	@observable private isShowMyBatchesOnly: boolean = false;

	// need to define separate instance of formModel because this needs to
	// be updated when adding/removing rows without updating cleanModel
	@observable
	protected benefitsPaymentsFormModel: FormModel;

	/**
	 * Used by View
	 * @private
	 */
	private benefitParametersTabSelected = true;
	/**
	 * Used by View
	 * @private
	 */
	private dispersalDefaultsTabSelected = false;

	/**
	 * Used by View
	 * @private
	 */
	private paymentDetailsTabSelected = false;

	/**
	 * Boolean flag used to indicate that slideout has been opened due to
	 * newly-created record.
	 */
	private recordOpenedOnCreate = false;

	private static readonly FUNCTION_NAME: string = 'benefits-payments';
	private static readonly REDEMPTION_INPUTS_FUNCTION_NAME: string =
		'redemption-inputs';
	private slideoutProgressBar: ProgressBar;

	private tabbedPanels: TabbedPanels;

	private static readonly MEMBER_PAYMENT_HISTORY_FUNCTION_NAME: string =
		'member-payment-history';

	constructor(
		public readonly dialogUtils: DialogUtils,
		public readonly classicPanelDialogUtils: ClassicPanelDialogUtils,
		private readonly taskQueue: TaskQueue,
		private readonly validationController: ValidationController,
		private readonly reportOutputUtils: ReportOutputUtils,
		private benefitsPaymentsCentreApi: BenefitsPaymentsCentreApi,
		private readonly customisationEditorSelectDialogManager: CustomisationEditorSelectDialogManager,
		...rest
	) {
		super(
			BenefitsPaymentsCentre.FUNCTION_NAME, // functionName
			true, // canSave
			false, // canDelete
			true, // hasNotes
			false, // hasAuditDetails
			true, // canAdd
			4, // visibleColumns
			[
				// all possible form parameters
				BatchProcessFieldName.BSz_Batch_Code,
				BatchProcessFieldName.BSz_Fund,
				BatchProcessFieldName.BSz_Member,
			],
			...rest,
		);

		this.contextMenuMaxItemsCount = 5;

		this.tableConfig.frontEndTableColumnsOverrides = {
			ZYl_Batch_Error_Level: {
				render: (fieldValue, columnInfo, record) =>
					this.renderBatchFlags(fieldValue, columnInfo, record),
				filter: createFilterModel({ hidden: true }),
			},
			ZYz_Batch_Progress_Status: {
				render: (fieldValue, columnInfo, row) =>
					this.renderStatus(fieldValue, columnInfo, row),
				filter: createFilterModel({ hidden: true }),
			},
			BSl_Master_Batch_Ref: {
				hiddenFrom: [HiddenTarget.TABLE],
			},
			[BatchProcessFieldName.ZYv_Correlation_ID]: {
				enableToolTip: true,
			},
		};
	}

	/**
	 * @override
	 */
	protected attached() {
		super.attached();
		this.subscribeToEvent(ClassicPanel.Events.PostPopulate, (data) => {
			if (
				this.recordSelectorTable &&
				this.recordSelectorTable.selectedObjId &&
				data.source === BenefitsPaymentsCentre.FUNCTION_NAME
			) {
				const fields = this.slideOut.formModel.fields;

				const totalValue: number = Number.parseInt(
					fields.ZYl_Batch_Progress_Total.value,
					10,
				);
				const currentValue: number = Number.parseInt(
					fields.ZYl_Batch_Progress_Current.value,
					10,
				);
				const batchProgressStatus: BatchProgressStatus = fields
					.ZYz_Batch_Progress_Status.value as BatchProgressStatus;

				this.slideoutProgressBar = this.getProgressBar(
					totalValue,
					currentValue,
					batchProgressStatus,
					new ProgressBar(undefined),
				);
				this.slideoutProgressBar.label = this.computeProgressBarLabel(
					totalValue,
					currentValue,
					batchProgressStatus,
				);
			}
		});

		this.subscribeToEvent(ClassicPanel.Events.PostSetIsEditing, (data) => {
			if (this.isPaymentDetailsEditable && !data.value) {
				this.slideOut.setIsEditing(true);
			}
		});
		this.bindCustomisationEditorShortcutKey(
			this.customisationEditorSelectDialogManager,
		);
	}

	private keyboardShortCutOverride(context: any) {
		KeyboardShortcut.subscribe(context, ShortcutKey.Close, (): void => {
			context.close();
		});
	}

	/**
	 * @override
	 */
	protected checkAuthAndOpenCustomisationEditorSelectDialog(
		customisationEditorSelectDialogManager: CustomisationEditorSelectDialogManager,
	) {
		const model: CustomisationEditorSelectDialogModel = {
			defaultFunctionName: this.functionName,
			areaFunctionNames: {
				displayRedemptionInputs:
					BenefitsPaymentsCentre.REDEMPTION_INPUTS_FUNCTION_NAME,
				addRedemptionInputs:
					BenefitsPaymentsCentre.REDEMPTION_INPUTS_FUNCTION_NAME,
				paymentOptionalItems:
					BenefitsPaymentsCentre.MEMBER_PAYMENT_HISTORY_FUNCTION_NAME,
			},
		};
		this.customisationEditorSelectDialogManager.checkAuthAndOpenCustomisationEditorSelectDialog(
			this.router,
			model,
		);
	}

	private computeProgressBarLabel(
		totalValue: number,
		currentValue: number,
		batchProgressStatus: BatchProgressStatus,
	): string {
		const stepRangeText = BenefitsPaymentsCentre.computeStepRangeText(
			totalValue,
			currentValue,
			batchProgressStatus,
		);
		if (stepRangeText !== '') {
			return this.i18n.tr('benefits-payments-centre:step-completed', {
				number: stepRangeText,
			});
		} else {
			switch (batchProgressStatus) {
				case BatchProgressStatus.COMPLETED:
					return this.i18n.tr(
						'benefits-payments-centre:all-steps-completed',
					);
				case BatchProgressStatus.CANCELLED:
					return this.i18n.tr(
						'benefits-payments-centre:batch-cancelled',
					);
				default:
					return '';
			}
		}
	}

	/**
	 * Filter table records according to batch code if `singleBatchCode` specified in customisation
	 * @override
	 */
	protected getFilteredParameterTypes(): FormParameterType[] {
		return [
			...super.getFilteredParameterTypes(),
			CommonURLParam.BATCHES,
			CommonURLParam.API_CORRELATION_ID,
		];
	}

	/**
	 * Registered the value of {@link CommonURLParam.API_CORRELATION_ID} to the filter
	 *
	 * @param params request parameters
	 */
	protected setSelectedParameters(params: {
		[paramName: string]: any;
	}): void {
		super.setSelectedParameters(params);
		const correlationId = params[CommonURLParam.API_CORRELATION_ID];
		if (correlationId) {
			this.selectedParameters.push({
				paramType: CommonURLParam.API_CORRELATION_ID,
				value: correlationId,
				name: BatchProcessFieldName.ZYv_Correlation_ID,
			});
		}
	}

	/**
	 * Render batch controls: notes; warnings/error; parent/child indicator
	 */
	private renderBatchFlags(_, __, row: BatchProcess): string {
		const result: IconStatusItem[] = [];

		// Note
		result.push(getNoteIconStatus(row.BSc_Notes === 'Y'));

		// Error / Warning
		const iconStatus: IconStatusError = row.ZYl_Batch_Error_Level;
		if (iconStatus !== null && iconStatus !== undefined) {
			result.push(getErrorOrWarningStatus(iconStatus));
		}

		// Master/Child indicator
		switch (row.ZYc_Master_Child_Indicator) {
			case BatchProgressParentChildIndicator.CHILD:
				result.push({
					title: this.i18n.tr('acurity-table.child'),
					class: 'fa fa-c',
				});
				break;
			case BatchProgressParentChildIndicator.PARENT:
				result.push({
					title: this.i18n.tr('acurity-table.parent'),
					class: 'fa fa-p',
				});
				break;
		}
		return IconStatusList(result);
	}

	/**
	 * Render batch status
	 */
	private renderStatus(_, __, row: BatchProcess) {
		// NOTE: Jake: 2019-10-02 - WEBUI-2494
		// This is a really nasty hack for rendering a React component inside
		// a DataTable. Please nobody copy this pattern.
		const progressBar = this.getProgressBarFromRowData(
			row,
			this.progressBar,
		);

		// if progress bar is empty, it does not matter what fill colour is
		// set fill colour to light so label colour becomes dark and can be seen
		if (progressBar.nowValue === 0) {
			progressBar.color = ProgressBar.ThemeColor.Light;
		}

		// Render and return outerHTML
		progressBar.render();
		return progressBar.getHTML();
	}

	private static computeStepRangeText(
		totalValue: number,
		currentValue: number,
		batchProgressStatus: BatchProgressStatus,
	): string {
		if (
			!(
				batchProgressStatus === BatchProgressStatus.COMPLETED ||
				batchProgressStatus === BatchProgressStatus.CANCELLED
			)
		) {
			return `${currentValue} \u2215 ${totalValue}`;
		}
		return '';
	}

	private getProgressBarFromRowData(
		row: BatchProcess,
		progressBar: ProgressBar,
	): ProgressBar {
		return this.getProgressBar(
			row.ZYl_Batch_Progress_Total,
			row.ZYl_Batch_Progress_Current,
			row.ZYz_Batch_Progress_Status,
			progressBar,
		);
	}

	private getProgressBar(
		totalValue: number,
		currentValue: number,
		batchProgressStatus: BatchProgressStatus,
		progressBar: ProgressBar,
	): ProgressBar {
		progressBar.maxValue = totalValue;
		progressBar.nowValue = currentValue;
		progressBar.label = BenefitsPaymentsCentre.computeStepRangeText(
			totalValue,
			currentValue,
			batchProgressStatus,
		);
		progressBar.animated = false;
		progressBar.striped = false;

		// DEBUG: Force a specific status to see how it looks without needing a real record
		// (need this silly if-statement otherwise TS compiler complains)
		// if (progressBar.label || !progressBar.label) {
		// 	batchProgressStatus = BatchProgressStatus.AWAITS_AUTHORISATION;
		// }

		let textStatus = '';
		switch (batchProgressStatus) {
			case BatchProgressStatus.AWAITS_AUTHORISATION:
				progressBar.color = ProgressBar.ThemeColor.Warning;
				progressBar.animated = true;
				progressBar.striped = true;
				textStatus = this.i18n.tr(
					'benefits-payments-centre:awaits-authorisation',
				);
				break;

			case BatchProgressStatus.CANCELLED:
				progressBar.color = ProgressBar.ThemeColor.Light;
				progressBar.nowValue = progressBar.maxValue;
				break;

			case BatchProgressStatus.COMPLETED:
				progressBar.color = ProgressBar.ThemeColor.Success;
				if (progressBar.maxValue === 0) {
					progressBar.maxValue = 1;
				}
				progressBar.nowValue = progressBar.maxValue;
				break;

			case BatchProgressStatus.ERROR:
				progressBar.color = ProgressBar.ThemeColor.Danger;
				textStatus = this.i18n.tr('notifications.error');
				break;

			case BatchProgressStatus.IN_PROGRESS_WITH_ERROR:
				progressBar.color = ProgressBar.ThemeColor.Danger;
				progressBar.animated = true;
				progressBar.striped = true;
				textStatus = this.i18n.tr('notifications.error');
				break;

			case BatchProgressStatus.IN_PROGRESS:
				progressBar.color = ProgressBar.ThemeColor.Success;
				progressBar.animated = true;
				progressBar.striped = true;
				break;

			default:
				const exhaustiveCheck: never = batchProgressStatus;
		}
		if (textStatus !== '') {
			if (progressBar.label) {
				progressBar.label += ' - ' + textStatus;
			} else {
				progressBar.label = textStatus;
			}
		}

		return progressBar;
	}

	/**
	 * Context menu config
	 * @override
	 */
	@computedFrom('contextMenuItemsClassicForm')
	public get contextMenuItems(): ContextMenuItem[] {
		if (this.contextMenuItemsClassicForm.length === 0) {
			// Do not render anything if contextMenuItemsClassicForm is empty.
			// This means it is still loading.
			return this.contextMenuItemsClassicForm;
		}
		const contextMenuItems: ContextMenuItem[] = [
			...this.contextMenuItemsClassicForm,
			...this.getCommonContextMenuItems(),
		];

		ApiCorrelationId.registerApiAuditLinkIn(
			contextMenuItems,
			this.i18n,
			this.formModel?.fields?.ZYv_Correlation_ID?.value,
		);

		return contextMenuItems;
	}

	/**
	 * @override
	 */
	@computedFrom('formModel', 'contextMenuIds')
	public get slideoutContextMenu(): ContextMenuItem[] {
		if (!this.formModel) {
			return [];
		}

		const formModelFields = this.formModel?.fields;
		const batchParams = {};
		if (formModelFields) {
			batchParams[CommonURLParam.BATCH_REF] =
				formModelFields.BSl_Batch_Reference.value;
			batchParams[CommonURLParam.BATCH_ID] =
				formModelFields.BSi_Identity.value;
			batchParams[CommonURLParam.FUND] = formModelFields.BSz_Fund.value;
			if (
				formModelFields.BSz_Member.value &&
				formModelFields.BSz_Member.value !== BLANK_MEMBER
			) {
				batchParams[CommonURLParam.MEMBER] =
					formModelFields.BSz_Member.value;
			}
		}
		const contextMenuItems: ContextMenuItem[] = [
			...this.getCommonContextMenuItems(),
			{
				id: this.contextMenuIds.reportingCentreMenu,
				label: this.i18n.tr('route-title.reporting-centre'),
				iconClass: 'fas fa-external-link-alt',
				href: generateLinkNoCustomisationParam(
					AppRouteID.ReportingCentre,
					batchParams,
				),
			},
			{
				id: this.contextMenuIds.transactionCentreMenu,
				label: this.i18n.tr('route-title.transaction-centre'),
				iconClass: 'fas fa-external-link-alt',
				href: generateLinkNoCustomisationParam(
					AppRouteID.TransactionCentre,
					batchParams,
				),
			},
			{
				id: this.contextMenuIds.redemptionInputsMemu,
				label: this.i18n.tr(
					'benefits-payments-centre:redemption-inputs',
				),
				iconClass: 'fas fa-external-link-alt',
				onClick: () => this.showRedemptionInputsDialog(),
			},
		];

		if (
			this.slideOut.formModel.fields.ZYi_Exit_Result_Report_Identity
				?.value
		) {
			contextMenuItems.push({
				id: this.contextMenuIds.transactionCentreMenu,
				label: this.i18n.tr(
					'benefits-payments-centre:calculation-results',
				),
				iconClass: 'fas fa-external-link-alt',
				onClick: () => this.displayCalculationResultsDialog(),
			});
		}

		contextMenuItems.push({
			id: this.contextMenuIds.restoreBatchMenu,
			label: this.i18n.tr('restore-batch'),
			iconClass: 'fa-undo',
			onClick: () => this.showRestoreBatchDialog(),
			disabled: !this.isRestoreBatchButtonVisible,
		});

		ApiCorrelationId.registerApiAuditLinkIn(
			contextMenuItems,
			this.i18n,
			this.formModel?.fields?.ZYv_Correlation_ID?.value,
		);

		return contextMenuItems;
	}

	private getCommonContextMenuItems(): ContextMenuItem[] {
		const formModelFields = this.formModel?.fields;
		const batchParams = {};
		if (formModelFields) {
			batchParams[CommonURLParam.BATCH_REF] =
				formModelFields.BSl_Batch_Reference.value;
			batchParams[CommonURLParam.BATCH_ENV] =
				formModelFields.BSc_Exit.value;
		}

		return [
			{
				id: this.contextMenuIds.batchMessagesMenu,
				label: this.i18n.tr('benefits-payments-centre:batch-messages'),
				iconClass: 'fas fa-external-link-alt',
				href: generateLinkNoCustomisationParam(
					AppRouteID.TransactionMessages,
					batchParams,
				),
			},
		];
	}

	/**
	 * Show dialog box when creating a new benefit payment
	 * @override
	 */
	protected openAddNewRecord = () => {
		if (!this.canAddRecord || this.formIsEditingRecord()) {
			return;
		}
		const newBenefitPaymentDialogModel: NewBenefitPaymentDialogModel =
			this.getClassicPanelDialogModel(FormType.ADD, 'newBenefitPayment');

		newBenefitPaymentDialogModel.onInsertSucceeded = (
			result: InsertResult,
		) => {
			this.recordOpenedOnCreate = true;
			this.openRecord(result.id);
		};

		this.classicPanelDialogUtils.showCustomClassicPanelDialog(
			NewBenefitPaymentDialog,
			newBenefitPaymentDialogModel,
			this.recordSelectorTable,
		);
	};

	/**
	 * Called bu Aurelia.
	 * Update 'myBatchesOnly' table filter
	 */
	private isShowMyBatchesOnlyChanged(value: boolean) {
		this.queryParams.set('myBatchesOnly', value.toString());
		this.refreshAjaxTableData();
	}

	/**
	 * Called bu Aurelia.
	 * Update 'showIncomplete' table filter
	 */
	private isShowIncompleteBatchesOnlyChanged(value: boolean) {
		this.queryParams.set('showIncompleteBatchesOnly', value.toString());
		this.refreshAjaxTableData();
	}

	/**
	 * Called by the view.
	 */
	private save() {
		return this.slideOut.save();
	}

	/**
	 * Called by the view.
	 */
	private edit() {
		return this.slideOut.edit();
	}

	/**
	 * Called by the view.
	 */
	private cancelEditOrClose() {
		return this.slideOut.cancelEditOrClose();
	}

	@computedFrom(
		'formModel.fields.BSc_Status.value',
		'formModel.fields.ZYl_Batch_Progress_Current.value',
	)
	public get hasBatchProcessCommenced() {
		return BenefitsPaymentsCentre.hasBatchProcessCommenced(this.formModel);
	}

	@computedFrom(
		'formModel.fields.BSc_Status.value',
		'formModel.fields.ZYc_Batch_Has_Run_Trans_35.value',
		'formModel.fields.ZYc_Untaxed_Categories_Exists.value',
		'formModel.fields.ZYf_Excess_Benefit.value',
	)
	public get isPaymentDetailsEditable() {
		return BenefitsPaymentsCentre.isPaymentDetailsEditable(this.formModel);
	}

	@computedFrom(
		'formModel',
		'formModel.fields.ZYc_Batch_Is_Before_Benefit_Calculations_Step.value',
		'formModel.fields.BSc_Status.value',
	)
	public get paymentDetailsTabDisabled() {
		const beforeBenefitCalculationsStep =
			'ZYc_Batch_Is_Before_Benefit_Calculations_Step';
		return (
			this.formModel &&
			this.formModel.fields[beforeBenefitCalculationsStep].value ===
				'Y' &&
			this.formModel.fields.BSc_Status.value !== 'C'
		);
	}

	private benifitParametersSaveFunction: () => Promise<void> = () => {
		const customApi =
			this.slideOut.panel.recordId + '/saveBenefitParameters';
		return this.slideOut.panel
			.save(customApi)
			.then((result) => {
				Notification.success(
					this.i18n.tr(
						'successes.this-record-was-successfully-updated',
					),
				);
			})
			.catch((error) => {
				const message = FetchUtils.normalizeError(error);
				const errorMessage = this.i18n.tr(
					'errors.unable-to-update-this-record-message',
					{ message },
				);
				FetchUtils.logAndDisplayError(errorMessage);
			});
	};

	private saveAndContinueProcessingBenefitPayment() {
		const batchIdentityValue =
			this.formModel.fields[BatchProcessFieldName.BSi_Identity].value;
		if (!batchIdentityValue) {
			throw new Error(
				"Process Benefit Payment Dialog: Unable to get 'BSi_Identity' value",
			);
		}

		let refresh = false;
		this.slideOut.save(() => {
			return this.slideOut.panel
				.save()
				.then((result) => {
					refresh = true;
					return BenefitsPaymentsCentreApi.processBenefitPayment(
						batchIdentityValue,
					);
				})
				.then((result) => {
					Notification.success(
						this.i18n.tr(
							'successes.this-record-was-successfully-updated',
						),
					);
					return result;
				})
				.catch((error) => {
					const message = FetchUtils.normalizeError(error);
					const errorMessage = this.i18n.tr(
						'errors.unable-to-update-this-record-message',
						{ message },
					);
					FetchUtils.logAndDisplayError(errorMessage);
				})
				.finally(() => {
					if (refresh) {
						this.refreshAjaxTableData();
						this.slideOut.reload();
					}
				});
		});
	}

	/**
	 * Show 'Restore Batch' dialog from record slideout
	 */
	private showRestoreBatchDialog = () => {
		const currentRecordId = Number(
			this.formModel.fields[BatchProcessFieldName.BSi_Identity].value,
		);

		const restoreBatchDialogModel: ClassicPanelDialogModel = {
			formType: FormType.EDIT,
			functionName:
				this.functionName + '/' + currentRecordId + '/restore',
			recordId: currentRecordId,
			queryParams: this.queryParams,
			/* Prevent restore batch dialogue from calling formModelChanged */
			onFormLoad: undefined,
			customisationConfig: this.customisationConfig,
			areaName: 'restoreBatch',
		};

		return this.classicPanelDialogUtils
			.showCustomClassicPanelDialogReturnOpen(
				RestoreBatchDialog,
				restoreBatchDialogModel,
			)
			.whenClosed((result) => {
				if (!result.wasCancelled) {
					this.recordSelectorTable.refreshAjaxTable(false);
					this.slideOut.reload();
				}
			});
	};

	@computedFrom(
		'formModel',
		'formModel.fields.ZYz_IsRestoreBatchAllowed.value',
	)
	public get isRestoreBatchButtonVisible() {
		return this.formModel?.fields.ZYz_IsRestoreBatchAllowed.value === 'Y';
	}

	/**
	 * Show 'Process Batch' dialog from record slideout
	 */
	private showProcessBatchDialog = () => {
		const batchRefFieldModel = FormModelUtils.fieldModelFrom({
			name: BatchProcessFieldName.BSl_Batch_Reference,
			label: 'Batch Reference',
			value: this.formModel.fields.BSl_Batch_Reference.value,
			modifiable: Modifiable.FALSE,
			visible: true,
		});
		const dialogModel: BenefitsPaymentsProcessBatchDialogModel = {
			batchRefFieldModel,
		};
		this.dialogUtils
			.showCustomDialog(BenefitsPaymentsProcessBatchDialog, dialogModel)
			.whenClosed((result) => {
				if (!result.wasCancelled) {
					this.saveAndContinueProcessingBenefitPayment();
					const jobRef =
						this.formModel.fields[BatchProcessFieldName.BSl_Job_Ref]
							.value;
					const jobQueue =
						this.formModel.fields[
							BatchProcessFieldName.BSz_Job_Queue
						].value;
					if (jobRef && jobQueue) {
						const jobPriority =
							this.formModel.fields[
								BatchProcessFieldName.ZYc_Job_Priority
							].value;
						Notification.info(
							this.i18n.tr(
								jobPriority
									? 'batch-processing-centre:job-launched-to-queue-with-priority'
									: 'batch-processing-centre:job-launched-to-queue',
								{
									ref: jobRef,
									queue: jobQueue,
									priority: jobPriority,
								},
							),
						);
					}
				}
			});
	};

	private onBenefitParametersContinue = (): void => {
		if (this.benefitParametersTabSelected) {
			const batchIdentityValue =
				this.formModel.fields[BatchProcessFieldName.BSi_Identity].value;
			if (!batchIdentityValue) {
				throw new Error(
					"Process Benefit Payment Dialog: Unable to get 'BSi_Identity' value",
				);
			}

			const customApi =
				this.slideOut.panel.recordId + '/saveBenefitParameters';

			this.slideOut.save(() => {
				this.formModel.fields.BSl_Batch_Reference.forceSave = false;
				const origFormModel = FormModelUtils.cloneModel(this.formModel);
				this.formModel.formModelList = [];
				return this.slideOut.panel
					.save(customApi)
					.then((result) => {
						Notification.success(
							this.i18n.tr(
								'successes.this-record-was-successfully-updated',
							),
						);
						this.tabbedPanels.selectNextTab();
					})
					.catch((error) => {
						const message = FetchUtils.normalizeError(error);
						const errorMessage = this.i18n.tr(
							'errors.unable-to-update-this-record-message',
							{ message },
						);
						FetchUtils.logAndDisplayError(errorMessage);
					})
					.finally(() => {
						this.formModel.fields.BSl_Batch_Reference.forceSave =
							true;
						this.formModel.formModelList =
							origFormModel.formModelList;
						this.benefitsPaymentsFormModel = this.formModel;
					});
			});
		}
	};

	private onDispersalDefaultsSave = (): void => {
		const batchIdentityValue =
			this.formModel.fields[BatchProcessFieldName.BSi_Identity].value;
		if (!batchIdentityValue) {
			throw new Error(
				"Process Benefit Payment Dialog: Unable to get 'BSi_Identity' value",
			);
		}

		const customApi =
			this.slideOut.panel.recordId + '/saveDispersalDefaults';

		this.slideOut.save(() => {
			const origFormModel = FormModelUtils.cloneModel(this.formModel);
			return this.slideOut.panel
				.save(customApi)
				.then((result) => {
					Notification.success(
						this.i18n.tr(
							'successes.this-record-was-successfully-updated',
						),
					);
				})
				.catch((error) => {
					const message = FetchUtils.normalizeError(error);
					const errorMessage = this.i18n.tr(
						'errors.unable-to-update-this-record-message',
						{ message },
					);
					FetchUtils.logAndDisplayError(errorMessage);
				})
				.finally(() => {
					// Reset form model field values to orig formmodel field values
					Object.keys(this.formModel.fields).forEach((key) => {
						this.formModel.fields[key].value =
							origFormModel.fields[key].value;
					});
				});
		});
	};

	private onPaymentDetailsSave = (): void => {
		const batchIdentityValue =
			this.formModel.fields[BatchProcessFieldName.BSi_Identity].value;
		if (!batchIdentityValue) {
			throw new Error(
				"Process Benefit Payment Dialog: Unable to get 'BSi_Identity' value",
			);
		}

		const customApi =
			this.slideOut.panel.recordId + '/save-payment-details';

		this.slideOut.save(() => {
			const origFormModel = FormModelUtils.cloneModel(this.formModel);
			return this.slideOut.panel
				.save(customApi)
				.then((result) => {
					Notification.success(
						this.i18n.tr(
							'successes.this-record-was-successfully-updated',
						),
					);
				})
				.catch((error) => {
					const message = FetchUtils.normalizeError(error);
					const errorMessage = this.i18n.tr(
						'errors.unable-to-update-this-record-message',
						{ message },
					);
					FetchUtils.logAndDisplayError(errorMessage);
				})
				.finally(() => {
					// Reset form model field values to orig formmodel field values
					Object.keys(this.formModel.fields).forEach((key) => {
						this.formModel.fields[key].value =
							origFormModel.fields[key].value;
					});
				});
		});
	};

	private displayCalculationResultsDialog() {
		const reportIdentity =
			this.slideOut.formModel.fields.ZYi_Exit_Result_Report_Identity
				.value;

		if (!reportIdentity) {
			Logger.warn(
				'Requested to show calculation results, but no report identity is given',
			);
			return;
		}

		const reportResult: ReportResult = {
			id: Number(reportIdentity),
			reportId: reportIdentity,
			reportOutputType: ReportOutputType.TEXT,
			reportRecordType: ReportRecordType.EXIT,
		};
		this.reportOutputUtils.displayReportOutput(reportResult);
	}
	/**
	 * Show 'Redemption Inputs' dialog from record slideout
	 */
	private showRedemptionInputsDialog = () => {
		const currentBatchRef = String(
			this.formModel.fields[BatchProcessFieldName.BSl_Batch_Reference]
				.value,
		);

		const currentFund = String(
			this.formModel.fields[BatchProcessFieldName.BSz_Fund].value,
		);

		const currentMember = String(
			this.formModel.fields[BatchProcessFieldName.BSz_Member].value,
		);

		const queryParameters = new Map<string, string>([
			['fund', currentFund],
			['member', currentMember],
		]);

		const preselectedParams = new Array<FormParameters>();
		preselectedParams.push({
			paramType: 'fund',
			value: currentFund,
			name: BatchProcessFieldName.BSz_Fund,
		});
		preselectedParams.push({
			paramType: 'member',
			value: currentMember,
			name: BatchProcessFieldName.BSz_Member,
		});

		const redemptionInputsDialogModel: RedemptionInputsDialogModel = {
			functionName:
				'member/' +
				currentFund +
				'/' +
				currentMember +
				'/redemption-inputs/batchRef/' +
				currentBatchRef,
			formType: FormType.ADD,
			headingText: this.i18n.tr(
				'benefits-payments-centre:redemption-inputs',
			),
			useViewEditBehaviour: false,
			batchRef: currentBatchRef,
			fund: currentFund,
			member: currentMember,
			queryParams: queryParameters,
			customisationConfig: this.customisationConfig,
		};

		return this.classicPanelDialogUtils.showCustomClassicPanelDialogReturnOpen(
			RedemptionInputsDialog,
			redemptionInputsDialogModel,
		);
	};

	protected formModelChanged(formModel: FormModel, oldValue?: FormModel) {
		super.formModelChanged(formModel, oldValue);
		if (!formModel) {
			return;
		}
		if (formModel.formModelList) {
			this.benefitParametersTabSelected = false;
			this.dispersalDefaultsTabSelected = false;
			this.paymentDetailsTabSelected = false;
			if (this.recordOpenedOnCreate) {
				this.recordOpenedOnCreate = false;
				this.benefitParametersTabSelected = true;
			} else if (this.paymentDetailsTabDisabled)
				this.dispersalDefaultsTabSelected = true;
			else this.paymentDetailsTabSelected = true;
			this.benefitsPaymentsFormModel = formModel;
		}
	}

	public static hasBatchProcessCommenced(formModel: FormModel) {
		return (
			formModel?.fields.BSc_Status.value !== '' ||
			(formModel?.fields.ZYl_Batch_Progress_Current &&
				parseInt(
					formModel?.fields.ZYl_Batch_Progress_Current.value,
					10,
				) > 0)
		);
	}

	public static isPaymentDetailsEditable(formModel: FormModel) {
		const isBatchStatusComplete =
			formModel?.fields.BSc_Status.value === 'C';
		const batchHasRunTrans35 =
			formModel?.fields.ZYc_Batch_Has_Run_Trans_35?.value === 'Y';
		const doesUntaxedCategoriesConfigExists =
			formModel?.fields.ZYc_Untaxed_Categories_Exists?.value === 'Y';
		const hasExcessBenefit =
			formModel?.fields.ZYf_Excess_Benefit &&
			parseInt(formModel?.fields.ZYf_Excess_Benefit.value, 10) !== 0;
		return (
			!isBatchStatusComplete &&
			!batchHasRunTrans35 &&
			!(doesUntaxedCategoriesConfigExists && hasExcessBenefit)
		);
	}
}
